import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const quizQuestions = [
  {
    id: 1,
    question: 'What is your main goal?',
    subtitle: 'Choose the area you want to focus on most',
    options: [
      { value: 'weight-loss', label: 'Weight Loss', desc: 'Reach and maintain a healthy weight' },
      { value: 'energy', label: 'Energy & Vitality', desc: 'Boost energy and mental clarity' },
      { value: 'wellness', label: 'Overall Wellness', desc: 'Comprehensive health optimization' }
    ]
  },
  {
    id: 2,
    question: 'Have you tried dieting or exercise programs before?',
    subtitle: 'Your experience helps us understand what might work best',
    options: [
      { value: 'yes-success', label: 'Yes, with some success', desc: 'But results did not last' },
      { value: 'yes-no-success', label: 'Yes, without success', desc: 'Nothing has worked for me' },
      { value: 'no', label: 'No, this is new for me', desc: 'Ready to start my journey' }
    ]
  },
  {
    id: 3,
    question: 'What is your biggest challenge right now?',
    subtitle: 'Select the one that resonates most with you',
    options: [
      { value: 'appetite', label: 'Managing appetite', desc: 'Constant cravings and hunger' },
      { value: 'energy', label: 'Low energy levels', desc: 'Too tired to stay active' },
      { value: 'motivation', label: 'Staying motivated', desc: 'Hard to stick with programs' },
      { value: 'results', label: 'Seeing results', desc: 'Efforts are not paying off' }
    ]
  },
  {
    id: 4,
    question: 'How soon are you looking to start?',
    subtitle: 'Help us prioritize your consultation',
    options: [
      { value: 'immediately', label: 'Immediately', desc: 'Ready to begin right away' },
      { value: 'week', label: 'Within a week', desc: 'Very interested, need details' },
      { value: 'month', label: 'Within a month', desc: 'Exploring my options' },
      { value: 'researching', label: 'Just researching', desc: 'Gathering information for now' }
    ]
  }
];

export const Quiz = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const totalSteps = quizQuestions.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowContactForm(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const validatePhoneNumber = (phone) => {
    const phoneDigits = phone.replace(/\D/g, '');
    return phoneDigits.length === 10;
  };

  const normalizePhoneNumber = (phone) => {
    // Normalize to (XXX) XXX-XXXX format for storage
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    return phone;
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setContactInfo({ ...contactInfo, phone: formatted });
      
      // Only show error if they've typed enough digits
      const phoneDigits = value.replace(/\D/g, '');
      if (phoneDigits.length > 0 && phoneDigits.length < 10) {
        setPhoneError('Phone number must be 10 digits');
      } else {
        setPhoneError('');
      }
    } else {
      setContactInfo({ ...contactInfo, [name]: value });
    }
    setError('');
  };

  const getRecommendation = () => {
    const mainGoal = answers[1];
    if (mainGoal === 'weight-loss') {
      return 'Based on your responses, GLP-1 medications or dual-action options may be a great fit for your weight loss goals.';
    } else if (mainGoal === 'energy') {
      return 'Based on your responses, NAD+ therapy combined with metabolic support could help boost your energy and vitality.';
    } else {
      return 'Based on your responses, a comprehensive wellness approach combining multiple modalities may be ideal for you.';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    setError('');
    setPhoneError('');

    // Validate phone number before submission
    if (!validatePhoneNumber(contactInfo.phone)) {
      setPhoneError('Please enter a valid 10-digit US phone number');
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare quiz data with normalized phone
      const quizData = {
        ...contactInfo,
        phone: normalizePhoneNumber(contactInfo.phone),
        primaryGoal: answers[1] || 'not-specified',
        interestedIn: 'quiz-funnel',
        notes: `Quiz Results:\n- Main Goal: ${answers[1]}\n- Previous Experience: ${answers[2]}\n- Biggest Challenge: ${answers[3]}\n- Timeline: ${answers[4]}\n\nRecommendation: ${getRecommendation()}`
      };

      const response = await axios.post(`${API}/leads`, quizData);

      if (response.status === 201) {
        navigate('/thank-you');
      }
    } catch (err) {
      console.error('Error submitting quiz:', err);
      const errorMessage = err.response?.data?.detail || 'Something went wrong. Please try again or call us at 801-651-8363';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  const currentQuestion = quizQuestions[currentStep];
  const currentAnswer = answers[currentQuestion?.id];
  const canProceed = currentAnswer !== undefined;

  if (showContactForm) {
    return (
      <div className="min-h-screen bg-[#0B1020] flex items-center justify-center px-4 py-12">
        <Card className="max-w-2xl w-full bg-[#121A2B] border-[#1E293B] p-8 sm:p-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#22C55E] to-[#16A34A] flex items-center justify-center mx-auto mb-4">
              <Sparkles className="text-white" size={32} />
            </div>
            <h2 className="text-3xl font-bold text-[#F8FAFC] mb-4">Your Personalized Recommendation</h2>
            <p className="text-[#94A3B8] mb-6">{getRecommendation()}</p>
            <div className="bg-[#0B1020] border border-[#1E293B] rounded-lg p-4 mb-8">
              <p className="text-sm text-[#94A3B8]">
                <strong className="text-[#F8FAFC]">Next Step:</strong> Share your contact information and Bryan will personally reach out within 24 hours to discuss your personalized plan.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4">
                <p className="text-red-400 text-sm">
                  {typeof error === 'string' ? error : 'Please check your information and try again.'}
                </p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className="text-[#F8FAFC]">First Name *</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  required
                  value={contactInfo.firstName}
                  onChange={handleContactChange}
                  className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] focus:border-[#3B82F6]"
                />
              </div>
              <div>
                <Label htmlFor="lastName" className="text-[#F8FAFC]">Last Name *</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  required
                  value={contactInfo.lastName}
                  onChange={handleContactChange}
                  className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] focus:border-[#3B82F6]"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-[#F8FAFC]">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={contactInfo.email}
                onChange={handleContactChange}
                className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] focus:border-[#3B82F6]"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-[#F8FAFC]">Phone *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                required
                placeholder="(555) 123-4567"
                value={contactInfo.phone}
                onChange={handleContactChange}
                className={`bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] focus:border-[#3B82F6] ${
                  phoneError ? 'border-red-500 focus:border-red-500' : ''
                }`}
                maxLength={14}
              />
              {phoneError && (
                <p className="text-red-400 text-sm mt-1">{phoneError}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white text-lg py-6 h-auto"
            >
              {isSubmitting ? 'Submitting...' : 'Get My Personalized Plan'}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1020] px-4 py-12">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex justify-between items-center mb-4">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-[#94A3B8] hover:text-[#F8FAFC]"
          >
            ← Back to Home
          </Button>
          <span className="text-[#94A3B8] text-sm">
            Question {currentStep + 1} of {totalSteps}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full h-2 bg-[#1E293B] rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#3B82F6] to-[#22C55E] transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question Card */}
      <div className="max-w-4xl mx-auto">
        <Card className="bg-[#121A2B] border-[#1E293B] p-8 sm:p-12">
          <div className="mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] mb-3">
              {currentQuestion.question}
            </h2>
            <p className="text-[#94A3B8]">{currentQuestion.subtitle}</p>
          </div>

          <RadioGroup value={currentAnswer} onValueChange={(value) => handleAnswer(currentQuestion.id, value)}>
            <div className="space-y-4">
              {currentQuestion.options.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start gap-4 p-6 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                    currentAnswer === option.value
                      ? 'border-[#3B82F6] bg-[#3B82F6]/10'
                      : 'border-[#1E293B] hover:border-[#3B82F6]/50 bg-[#0B1020]'
                  }`}
                >
                  <RadioGroupItem value={option.value} className="mt-1" />
                  <div className="flex-1">
                    <div className="font-bold text-[#F8FAFC] text-lg mb-1">{option.label}</div>
                    <div className="text-[#94A3B8] text-sm">{option.desc}</div>
                  </div>
                  {currentAnswer === option.value && (
                    <CheckCircle2 className="text-[#3B82F6] flex-shrink-0" size={24} />
                  )}
                </label>
              ))}
            </div>
          </RadioGroup>

          {/* Navigation */}
          <div className="flex gap-4 mt-8">
            {currentStep > 0 && (
              <Button
                onClick={handleBack}
                variant="outline"
                className="border-[#1E293B] bg-transparent hover:bg-[#1E293B] text-[#F8FAFC]"
              >
                <ArrowLeft className="mr-2" size={18} />
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!canProceed}
              className={`flex-1 text-lg py-6 h-auto ${
                canProceed
                  ? 'bg-[#3B82F6] hover:bg-[#2563EB] text-white'
                  : 'bg-[#1E293B] text-[#94A3B8] cursor-not-allowed'
              }`}
            >
              {currentStep === totalSteps - 1 ? 'See My Results' : 'Next'}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Quiz;
